// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import BlockCard from "@flip/App/Widgets/ContentBlock/BlockCard";
import systemColors from "@flip/XDL/PreStyle/colors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import FieldDefinition, { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omit from "lodash/omit";
import * as React from "react";
import { css } from "styled-components";

const enum blockCardItemFields {
    image = "image",
    isMobileSpecific = "isMobileSpecific",
    mobileSpecificImage = "mobileSpecificImage",
    imagePosition = "imagePosition",
    title = "title",
    description = "description",
    descriptionColor = "descriptionColor",
    buttonVariant = "buttonVariant",
    buttonLabel = "buttonLabel",
    link = "link",
    backgroundColor = "backgroundColor",
}

interface BlockCardItemProps {
    fields: {
        [blockCardItemFields.image]: string;
        [blockCardItemFields.isMobileSpecific]?: boolean;
        [blockCardItemFields.mobileSpecificImage]?: string;
        [blockCardItemFields.imagePosition]: "left" | "right";
        [blockCardItemFields.title]: string;
        [blockCardItemFields.description]: string;
        [blockCardItemFields.descriptionColor]: "black" | "white";
        [blockCardItemFields.buttonVariant]: "primary" | "secondary" | "tertiary" | "link";
        [blockCardItemFields.buttonLabel]: string;
        [blockCardItemFields.link]: LinkFieldValue;
        [blockCardItemFields.backgroundColor]: string;
    };
}

const enum fields {
    blocks = "blocks",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.blocks]: BlockCardItemProps[];
    };
}

type Props = OwnProps;

export interface ContentBlockStyles {
    container?: InjectableCss;
}

export const contentBlockStyles: ContentBlockStyles = {
    container: {
        css: css`
            position: relative;
        `,
    },
};

const styles = contentBlockStyles;

const ContentBlock: React.FC<Props> = ({ fields }) => {
    const blocks = ([{ fields: { ...omit(fields, ["blocks"]) } }] as BlockCardItemProps[])

    return (
        <StyledWrapper {...styles.container}  data-test-selector="Keller-ContentBlock">
            {blocks.map(block => {
                return (
                    <BlockCard
                        key={`ContentBlock_${block.fields.title}`}
                        image={block.fields.image}
                        imagePosition={block.fields.imagePosition}
                        title={block.fields.title}
                        description={block.fields.description}
                        descriptionColor={block.fields.descriptionColor}
                        buttonVariant={block.fields.buttonVariant}
                        buttonLabel={block.fields.buttonLabel}
                        link={block.fields.link}
                        mobileSpecificImage={block.fields.mobileSpecificImage}
                        backgroundColor={block.fields.backgroundColor}
                    />
                );
            })}
        </StyledWrapper>
    );
};

const cardFieldDefinitions = (): FieldDefinition[] => {
    return [
        {
            name: blockCardItemFields.title,
            displayName: "Title",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: false,
            fieldType: "General",
        },
        {
            name: blockCardItemFields.description,
            displayName: "Description",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            isRequired: true,
            fieldType: "General",
        },
        {
            name: blockCardItemFields.descriptionColor,
            displayName: "Description Color",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Black", value: "black" },
                { displayName: "White", value: "white" },
            ],
            hideEmptyOption: true,
            defaultValue: "black",
            fieldType: "General",
        },
        {
            name: blockCardItemFields.image,
            displayName: "Image",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            isRequired: true,
            fieldType: "General",
        },
        {
            name: blockCardItemFields.isMobileSpecific,
            displayName: "Mobile Specific Image",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            variant: "toggle",
            fieldType: "General",
        },
        {
            name: blockCardItemFields.mobileSpecificImage,
            displayName: "Mobile Image",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            isVisible: item => item.fields.isMobileSpecific,
            isRequired: true,
            fieldType: "General",
        },
        {
            name: blockCardItemFields.imagePosition,
            displayName: "Image Position",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Left", value: "left" },
                { displayName: "Right", value: "right" },
            ],
            hideEmptyOption: true,
            defaultValue: "right",
            fieldType: "General",
        },
        {
            name: blockCardItemFields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: (systemColors as any).common.ui10,
            fieldType: "General",
        },
        {
            name: blockCardItemFields.buttonLabel,
            editorTemplate: "TextField",
            displayName: "Button Label",
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: blockCardItemFields.link,
            displayName: "Button Link",
            editorTemplate: "LinkField",
            defaultValue: { value: "", type: "Url" },
            fieldType: "General",
        },
        {
            name: blockCardItemFields.buttonVariant,
            displayName: "Button Variant",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Primary", value: "primary" },
                { displayName: "Secondary", value: "secondary" },
                { displayName: "Tertiary", value: "tertiary" },
                { displayName: "Link", value: "link" },
            ],
            hideEmptyOption: true,
            defaultValue: "primary",
            fieldType: "General",
        },
    ];
};

const widgetModule: WidgetModule = {
    component: ContentBlock,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [...cardFieldDefinitions()],
    },
};

export default widgetModule;
